import { useTranslations } from "next-intl";
import type { ReactNode } from "react";
import { MobileHeading } from "./pricing-mobile-hero";
import {
  BASE_FEE_ICON,
  CheckMark,
  HEADING_RICH_TAGS,
  InfinityIcon,
  T,
  includedCategories,
  includedIntro,
  supportFeeSub,
  supportFeeTitle,
  unlimitedRows,
} from "./pricing-shared";
import { PricingSupportFeeTiers } from "./pricing-support-fee-tiers";

const HeadIcon = BASE_FEE_ICON;

function CatHeader({
  icon,
  title,
  sub,
  count,
}: {
  readonly icon: ReactNode;
  readonly title: string;
  readonly sub: string;
  readonly count?: string;
}): ReactNode {
  return (
    <div
      style={{
        display: "flex",
        gap: 13,
        alignItems: "flex-start",
        marginBottom: 4,
      }}
    >
      <span
        style={{
          width: 40,
          height: 40,
          borderRadius: 9,
          background: "#EFEBE2",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          flex: "none",
        }}
      >
        {icon}
      </span>
      <div>
        <h3
          style={{
            fontFamily: T.display,
            fontWeight: 600,
            fontSize: count ? 21 : 22,
            lineHeight: 1.1,
            color: T.ink,
            margin: 0,
          }}
        >
          {title}
        </h3>
        <p
          style={{
            fontFamily: T.sans,
            fontSize: 13.5,
            lineHeight: 1.5,
            color: "#575B63",
            margin: "6px 0 0",
          }}
        >
          {sub}
        </p>
        {count ? (
          <span
            style={{
              display: "inline-block",
              marginTop: 8,
              fontFamily: T.label,
              fontSize: 10,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              color: "#9AA0A6",
            }}
          >
            {count}
          </span>
        ) : null}
      </div>
    </div>
  );
}

const ITEM_ROW = {
  display: "flex",
  gap: 11,
  alignItems: "center",
  borderTop: "1px solid rgba(11,15,25,0.06)",
} as const;

/** Mobile "Das ist alles dabei" catalog (single column). */
export function MobileIncluded(): ReactNode {
  const tPricingShared = useTranslations("public.pricingShared");
  const t = useTranslations("public.pricingMobileIncluded");
  return (
    <div
      id="enthalten"
      data-screen-label={t("enthalten")}
      style={{ padding: "18px 20px 56px" }}
    >
      <MobileHeading
        title={t.rich("allesDabei", HEADING_RICH_TAGS)}
        lead={includedIntro(tPricingShared)}
      />

      <div
        style={{
          background: "#FAFAFA",
          border: "1px solid rgba(11,15,25,0.10)",
          borderRadius: 14,
          padding: "20px 18px",
        }}
      >
        <CatHeader
          icon={<InfinityIcon size={20} />}
          title={t("keinAufpreisProKopf")}
          sub={t("egalVieleMitarbeiterZugaenge")}
          count={t("unbegrenzt")}
        />
        {unlimitedRows(tPricingShared).map((row) => (
          <div key={row.label} style={{ ...ITEM_ROW, padding: "11px 0" }}>
            <InfinityIcon size={15} strokeWidth={2.2} />
            <span
              style={{
                fontFamily: T.sans,
                fontSize: 14.5,
                fontWeight: 500,
                color: "#3F444D",
                whiteSpace: "nowrap",
              }}
            >
              {row.label}
            </span>
            <span
              style={{
                marginLeft: "auto",
                fontFamily: T.sans,
                fontSize: 13,
                color: "#8A8D94",
                textAlign: "right",
              }}
            >
              {row.value}
            </span>
          </div>
        ))}
      </div>

      {includedCategories(tPricingShared).map((cat) => {
        const Icon = cat.icon;
        return (
          <div
            key={cat.title}
            style={{
              padding: "26px 0 4px",
              borderTop: "1px solid rgba(11,15,25,0.10)",
              marginTop: 22,
            }}
          >
            <CatHeader
              icon={
                <Icon
                  size={18}
                  strokeWidth={1.7}
                  color="#3F444D"
                  aria-hidden="true"
                />
              }
              title={cat.title}
              sub={cat.sub}
              count={cat.count}
            />
            {cat.items.map((item) => (
              <div
                key={item.name}
                style={{
                  ...ITEM_ROW,
                  alignItems: "flex-start",
                  padding: "10px 0",
                }}
              >
                {/* Box = one text line, so the check sits on line 1 when the name wraps. */}
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    height: 20,
                    flex: "none",
                  }}
                >
                  <CheckMark />
                </span>
                <span
                  style={{
                    flex: 1,
                    fontFamily: T.sans,
                    fontSize: 14.5,
                    lineHeight: "20px",
                    fontWeight: 500,
                    color: "#3F444D",
                  }}
                >
                  {item.name}
                </span>
                {item.bald ? (
                  <span
                    style={{
                      alignSelf: "center",
                      fontFamily: T.label,
                      fontSize: 9,
                      letterSpacing: "0.1em",
                      textTransform: "uppercase",
                      color: "#8A8D94",
                      border: "1px solid rgba(11,15,25,0.16)",
                      borderRadius: 999,
                      padding: "2px 7px",
                    }}
                  >
                    {t("bald")}
                  </span>
                ) : null}
              </div>
            ))}
          </div>
        );
      })}

      <div
        style={{
          padding: "26px 0 0",
          borderTop: "1px solid rgba(11,15,25,0.10)",
          marginTop: 22,
        }}
      >
        <CatHeader
          icon={
            <HeadIcon
              size={18}
              strokeWidth={1.7}
              color="#3F444D"
              aria-hidden="true"
            />
          }
          title={supportFeeTitle(tPricingShared)}
          sub={supportFeeSub(tPricingShared)}
        />
        <div style={{ marginTop: 14 }}>
          <PricingSupportFeeTiers compact />
        </div>
      </div>
    </div>
  );
}
